'use client';
import React from 'react';
import { X, Zap, Crown, Star, ArrowRight } from 'lucide-react';
import Link from 'next/link';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  signalsUsed?: number;
  signalsLimit?: number;
}

export default function PaywallModal({
  isOpen,
  onClose,
  signalsUsed = 5,
  signalsLimit = 5,
}: PaywallModalProps) {
  if (!isOpen) return null;

  const plans = [
    {
      id: 'weekly',
      label: 'WEEKLY ACCESS',
      badge: null,
      price: '$19',
      period: '/week',
      description: 'Perfect to test premium signals',
      features: [
        '10–20 signals per day',
        'Full access to premium signals',
        'More trading opportunities',
        'Ideal for short-term testing',
      ],
      accentColor: 'amber',
      borderClass: 'border-amber-500/30',
      bgClass: 'bg-amber-500/5',
      badgeClass: '',
      buttonStyle: {
        background: 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 50%, #B8961F 100%)',
      },
    },
    {
      id: 'monthly',
      label: 'MONTHLY ACCESS',
      badge: '🔥 BEST VALUE',
      price: '$59',
      period: '/month',
      description: 'Built for consistent traders',
      features: [
        '10–20 signals per day',
        'Full premium access',
        'Consistent signal flow',
        'Best cost per week',
        'Priority experience',
      ],
      accentColor: 'violet',
      borderClass: 'border-violet-500/40',
      bgClass: 'bg-violet-500/5',
      badgeClass: 'bg-violet-500/20 text-violet-300 border border-violet-500/30',
      buttonStyle: {
        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #6d28d9 100%)',
      },
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-zinc-950 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/5 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 text-center border-b border-white/5">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 mb-3">
            <Crown className="w-6 h-6 text-amber-400" />
          </div>
          <h2 className="text-xl font-bold text-zinc-100 tracking-tight">Limit Reached</h2>
          <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
            You&apos;ve reached your free signal limit.
            <br />
            Upgrade to continue generating signals.
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-900 border border-white/5">
            <Zap className="w-3 h-3 text-amber-400" />
            <span className="text-xs text-zinc-400">
              <span className="text-zinc-200 font-medium">{signalsUsed}/{signalsLimit}</span> signals used today
            </span>
          </div>
        </div>

        {/* Plans */}
        <div className="p-4 space-y-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-xl border ${plan.borderClass} ${plan.bgClass} p-4`}
            >
              {plan.badge && (
                <span className={`absolute -top-2.5 left-4 text-xs font-semibold px-2.5 py-0.5 rounded-full ${plan.badgeClass}`}>
                  {plan.badge}
                </span>
              )}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-xs font-semibold text-zinc-500 tracking-widest uppercase">
                    {plan.label}
                  </p>
                  <div className="flex items-baseline gap-1 mt-0.5">
                    <span className="text-2xl font-bold text-zinc-100">{plan.price}</span>
                    <span className="text-xs text-zinc-500">{plan.period}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">{plan.description}</p>
                </div>
                <Star className="w-4 h-4 text-zinc-600 mt-1 flex-shrink-0" />
              </div>

              <ul className="space-y-1 mb-3">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-1.5 text-xs text-zinc-400">
                    <span className="text-emerald-400 font-bold">✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                href="/pricing"
                onClick={onClose}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-semibold text-zinc-950 transition-all duration-150 active:scale-95 hover:opacity-90"
                style={plan.buttonStyle}
              >
                {plan.id === 'weekly' ? 'Upgrade Weekly' : 'Get Monthly Access'}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 text-center">
          <p className="text-xs text-zinc-600">
            This platform provides trading signals only. You execute the trades and manage your own risk.
          </p>
        </div>
      </div>
    </div>
  );
}
